import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { formatEatDate, formatMoney, moneyFromCents } from '@wodi/domain';
import type { ServiceItemDetailView } from '@wodi/shared';
import {
  Button,
  DateField,
  LoadingBlock,
  MoneyField,
  Notice,
  PageHeader,
  RadioGroup,
  Tag,
  TextArea,
  TextField,
} from '@wodi/ui';
import { useState } from 'react';

import { ApiError } from '../api/client.ts';
import {
  addItemCode,
  changePrice,
  itemQuery,
  setAvailability,
  setItemActive,
  updateItem,
  withdrawPrice,
} from '../api/finance.ts';
import { can, sessionQuery } from '../api/session.ts';
import { CATEGORY_LABELS, CODE_SYSTEM_LABELS, KIND_LABELS, day, money } from '../finance/labels.ts';
import { useOnline } from '../hooks/use-online.ts';
import styles from '../patients/patients.module.css';
import { toasts } from '../toasts.ts';
import { SettingsPage } from './settings-nav.tsx';

// One service or item (docs/plans/phase1-finance-catalogue.md §6.2): details, prices per
// list with their schedule, codes, and where it is offered. Nothing is deleted.

type PriceBlock = ServiceItemDetailView['prices'][number];

function message(error: unknown, fallback: string): string {
  return error instanceof ApiError ? error.message : fallback;
}

/** Today's date in EAT as `YYYY-MM-DD`, the earliest a change can start. */
function todayIso(now = new Date()): string {
  const eat = new Date(now.getTime() + 3 * 60 * 60 * 1000);
  return eat.toISOString().slice(0, 10);
}

function ChangePrice({
  itemId,
  block,
  onDone,
}: {
  itemId: string;
  block: PriceBlock;
  onDone: () => void;
}) {
  const queryClient = useQueryClient();
  const [cents, setCents] = useState<bigint | null>(null);
  const [amountError, setAmountError] = useState<string | null>(null);
  const [when, setWhen] = useState<'now' | 'day'>('now');
  const [startDay, setStartDay] = useState<string | null>(null);
  const [confirmFree, setConfirmFree] = useState(false);
  const [tried, setTried] = useState(false);
  const change = useMutation({
    mutationFn: () =>
      changePrice(block.priceListId, {
        serviceItemId: itemId,
        unitPrice: { currency: 'KES', cents: (cents ?? 0n).toString() },
        startsOn: when === 'now' ? 'now' : (startDay ?? ''),
        expectedCurrentPriceId: block.priceId,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['catalogue'] });
      await queryClient.invalidateQueries({ queryKey: ['price-lists'] });
      toasts.show({ title: 'Price changed', description: block.priceListName });
      onDone();
    },
  });
  const newPrice = cents === null ? null : formatMoney(moneyFromCents(cents));
  const startsText =
    when === 'now'
      ? 'From now'
      : startDay === null
        ? null
        : `From ${day(`${startDay}T00:00:00+03:00`)}`;
  const preview =
    newPrice === null || startsText === null
      ? null
      : `${startsText}, the ${block.priceListName} price becomes ${newPrice}.${
          block.price !== null && when === 'day'
            ? ` Until then it stays ${money(block.price)}.`
            : ''
        }`;
  const isFree = cents === 0n;
  return (
    <form
      className={styles.inlineForm}
      aria-label={`Change ${block.priceListName} price`}
      onSubmit={(event) => {
        event.preventDefault();
        setTried(true);
        if (cents === null || amountError !== null) return;
        if (when === 'day' && startDay === null) return;
        if (isFree && !confirmFree) return;
        change.mutate();
      }}
    >
      <MoneyField
        label={`New ${block.priceListName} price`}
        value={cents}
        isRequired
        onChange={(value, error) => {
          setCents(value);
          setAmountError(error);
          setConfirmFree(false);
        }}
        {...(tried && cents === null && amountError === null
          ? { errorMessage: 'Enter the new price.' }
          : {})}
      />
      <RadioGroup
        label="Starts"
        value={when}
        options={[
          { id: 'now', label: 'Now', description: 'Charges from now on use the new price.' },
          { id: 'day', label: 'On a later day', description: 'From 00:00 that day.' },
        ]}
        onChange={(value) => {
          setWhen(value as 'now' | 'day');
        }}
      />
      {when === 'day' ? (
        <DateField
          label="Start day"
          value={startDay}
          minValue={todayIso()}
          onChange={setStartDay}
          {...(tried && startDay === null ? { errorMessage: 'Choose the day it starts.' } : {})}
        />
      ) : null}
      {preview === null ? null : <p>{preview}</p>}
      {isFree ? (
        <Notice tone="info">
          {confirmFree
            ? 'This service will be free on this list.'
            : 'A price of zero makes this free on this list. Choose "Make it free" to confirm.'}
        </Notice>
      ) : null}
      {change.error === null ? null : (
        <Notice tone="error">
          {message(change.error, "The price wasn't changed. Try again.")}
        </Notice>
      )}
      <div className={styles.actions}>
        {isFree && !confirmFree ? (
          <Button
            variant="primary"
            onPress={() => {
              setConfirmFree(true);
            }}
          >
            Make it free
          </Button>
        ) : (
          <Button
            type="submit"
            variant="primary"
            isPending={change.isPending}
            pendingLabel="Changing…"
          >
            Change price
          </Button>
        )}
        <Button variant="quiet" onPress={onDone}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

function WithdrawScheduled({
  priceId,
  listName,
  onDone,
}: {
  priceId: string;
  listName: string;
  onDone: () => void;
}) {
  const queryClient = useQueryClient();
  const [reason, setReason] = useState('');
  const [tried, setTried] = useState(false);
  const withdraw = useMutation({
    mutationFn: () => withdrawPrice(priceId, reason.trim()),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['catalogue'] });
      toasts.show({ title: 'Scheduled price withdrawn', description: listName });
      onDone();
    },
  });
  return (
    <form
      className={styles.inlineForm}
      aria-label="Withdraw scheduled price"
      onSubmit={(event) => {
        event.preventDefault();
        setTried(true);
        if (reason.trim().length >= 5) withdraw.mutate();
      }}
    >
      <TextArea
        label="Why is it being withdrawn?"
        description="Kept with the price history."
        value={reason}
        onChange={setReason}
        {...(tried && reason.trim().length < 5
          ? { errorMessage: 'Give a reason of at least 5 characters.' }
          : {})}
      />
      {withdraw.error === null ? null : (
        <Notice tone="error">{message(withdraw.error, "It wasn't withdrawn. Try again.")}</Notice>
      )}
      <div className={styles.actions}>
        <Button type="submit" variant="destructive" isPending={withdraw.isPending}>
          Withdraw price
        </Button>
        <Button variant="quiet" onPress={onDone}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

function PricesSection({
  item,
  canPrice,
  online,
}: {
  item: ServiceItemDetailView;
  canPrice: boolean;
  online: boolean;
}) {
  const [changing, setChanging] = useState<string | null>(null);
  const [withdrawing, setWithdrawing] = useState<string | null>(null);
  return (
    <section className={styles.section} aria-labelledby="prices-heading">
      <div className={styles.sectionHeader}>
        <h2 id="prices-heading">Prices</h2>
      </div>
      {item.prices.length === 0 ? (
        <p>No price lists yet. Create a price list first.</p>
      ) : (
        <ul className={styles.recordList}>
          {item.prices.map((block) => (
            <li key={block.priceListId} className={styles.recordItem}>
              <span>{block.priceListName}</span>
              <span className="wodi-num">{money(block.price)}</span>
              {block.effectiveFrom === null ? null : (
                <span className={styles.meta}>{`Since ${day(block.effectiveFrom)}`}</span>
              )}
              {block.scheduled === null ? null : (
                <span className={styles.meta}>
                  {`${money(block.scheduled.price)} from ${day(block.scheduled.effectiveFrom)}`}
                </span>
              )}
              {canPrice ? (
                <span className={styles.actions}>
                  {block.scheduled === null ? (
                    <Button
                      dense
                      variant="quiet"
                      isDisabled={!online}
                      aria-label={`Change ${block.priceListName} price`}
                      onPress={() => {
                        setChanging(block.priceListId);
                      }}
                    >
                      Change price
                    </Button>
                  ) : (
                    <Button
                      dense
                      variant="quiet"
                      isDisabled={!online}
                      aria-label={`Withdraw the scheduled ${block.priceListName} price`}
                      onPress={() => {
                        setWithdrawing(block.scheduled?.priceId ?? null);
                      }}
                    >
                      Withdraw scheduled price
                    </Button>
                  )}
                </span>
              ) : null}
              {changing === block.priceListId ? (
                <ChangePrice
                  itemId={item.id}
                  block={block}
                  onDone={() => {
                    setChanging(null);
                  }}
                />
              ) : null}
              {block.scheduled !== null && withdrawing === block.scheduled.priceId ? (
                <WithdrawScheduled
                  priceId={block.scheduled.priceId}
                  listName={block.priceListName}
                  onDone={() => {
                    setWithdrawing(null);
                  }}
                />
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function CodesSection({
  item,
  canManage,
  online,
}: {
  item: ServiceItemDetailView;
  canManage: boolean;
  online: boolean;
}) {
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [code, setCode] = useState('');
  const add = useMutation({
    mutationFn: () =>
      addItemCode(item.id, {
        system: 'legacy',
        systemVersion: 'local',
        code: code.trim(),
        display: item.name,
        source: 'manual',
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['catalogue'] });
      toasts.show({ title: 'Code added', description: code.trim() });
      setAdding(false);
      setCode('');
    },
  });
  const hasOwnCode = item.codes.some((c) => c.system === 'legacy' && c.status === 'active');
  return (
    <section className={styles.section} aria-labelledby="codes-heading">
      <div className={styles.sectionHeader}>
        <h2 id="codes-heading">Codes</h2>
        {canManage && !hasOwnCode && !adding ? (
          <Button
            dense
            isDisabled={!online}
            onPress={() => {
              setAdding(true);
            }}
          >
            Add own code
          </Button>
        ) : null}
      </div>
      <p className={styles.meta}>
        SHA, KNHTS, LOINC and ATC codes come from the national terminology or an official import,
        never typed by hand. The terminology connection isn't set up yet.
      </p>
      {item.codes.length === 0 ? <p>No codes yet.</p> : null}
      <ul className={styles.recordList}>
        {item.codes.map((c) => (
          <li
            key={c.id}
            className={styles.recordItem}
            {...(c.status === 'entered_in_error' ? { 'data-entered-in-error': true } : {})}
          >
            <span className="wodi-id">{c.code}</span>
            <span>{CODE_SYSTEM_LABELS[c.system]}</span>
            <span>{c.display}</span>
            <span className={styles.meta}>{`Version ${c.systemVersion}`}</span>
            {c.status === 'entered_in_error' ? (
              <span className={styles.meta}>{`Entered in error: ${c.statusReason ?? ''}`}</span>
            ) : null}
          </li>
        ))}
      </ul>
      {adding ? (
        <form
          className={styles.inlineForm}
          aria-label="Add own code"
          onSubmit={(event) => {
            event.preventDefault();
            if (code.trim() !== '') add.mutate();
          }}
        >
          <TextField
            label="Own code"
            description="The code your old system used, so staff can still find it."
            value={code}
            onChange={setCode}
          />
          {add.error === null ? null : (
            <Notice tone="error">{message(add.error, "It wasn't added. Try again.")}</Notice>
          )}
          <div className={styles.actions}>
            <Button type="submit" variant="primary" isPending={add.isPending}>
              Add code
            </Button>
            <Button
              variant="quiet"
              onPress={() => {
                setAdding(false);
              }}
            >
              Cancel
            </Button>
          </div>
        </form>
      ) : null}
    </section>
  );
}

function AvailabilitySection({
  item,
  canManage,
  online,
}: {
  item: ServiceItemDetailView;
  canManage: boolean;
  online: boolean;
}) {
  const queryClient = useQueryClient();
  const [stopping, setStopping] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const save = useMutation({
    mutationFn: (input: { facilityId: string; offered: boolean }) =>
      setAvailability(item.id, input.facilityId, {
        offered: input.offered,
        reason: input.offered ? null : reason.trim(),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['catalogue'] });
      setStopping(null);
      setReason('');
    },
  });
  return (
    <section className={styles.section} aria-labelledby="offered-heading">
      <div className={styles.sectionHeader}>
        <h2 id="offered-heading">Offered at</h2>
      </div>
      <ul className={styles.recordList}>
        {item.availability.map((a) => (
          <li key={a.facilityId} className={styles.recordItem}>
            <span>{a.facilityName}</span>
            <Tag state={a.offered ? 'active' : 'neutral'}>
              {a.offered ? 'Offered' : 'Not offered here'}
            </Tag>
            {a.reason === null ? null : <span className={styles.meta}>{a.reason}</span>}
            {canManage ? (
              <Button
                dense
                variant="quiet"
                isDisabled={!online || save.isPending}
                aria-label={`${a.offered ? 'Stop offering' : 'Offer'} at ${a.facilityName}`}
                onPress={() => {
                  if (a.offered) setStopping(a.facilityId);
                  else save.mutate({ facilityId: a.facilityId, offered: true });
                }}
              >
                {a.offered ? 'Stop offering here' : 'Offer here'}
              </Button>
            ) : null}
            {stopping === a.facilityId ? (
              <form
                className={styles.inlineForm}
                aria-label={`Stop offering at ${a.facilityName}`}
                onSubmit={(event) => {
                  event.preventDefault();
                  if (reason.trim().length >= 5)
                    save.mutate({ facilityId: a.facilityId, offered: false });
                }}
              >
                <TextField
                  label="Why isn't it offered here?"
                  description="Shown to staff who look for it."
                  value={reason}
                  onChange={setReason}
                />
                <div className={styles.actions}>
                  <Button type="submit" variant="primary" isPending={save.isPending}>
                    Stop offering
                  </Button>
                  <Button
                    variant="quiet"
                    onPress={() => {
                      setStopping(null);
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            ) : null}
          </li>
        ))}
      </ul>
      {save.error === null ? null : (
        <Notice tone="error">{message(save.error, "That change wasn't saved. Try again.")}</Notice>
      )}
    </section>
  );
}

function StatusSection({ item, online }: { item: ServiceItemDetailView; online: boolean }) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const toggle = useMutation({
    mutationFn: () =>
      setItemActive(item.id, item.status !== 'active', {
        expectedVersion: item.version,
        ...(item.status === 'active' ? { reason: reason.trim() } : {}),
      }),
    onSuccess: async (after) => {
      await queryClient.invalidateQueries({ queryKey: ['catalogue'] });
      toasts.show({
        title: after.status === 'active' ? 'Back in use' : 'No longer in use',
        description: after.name,
      });
      setOpen(false);
    },
  });
  return (
    <div className={styles.actions}>
      {item.status === 'active' && !open ? (
        <Button
          variant="quiet"
          isDisabled={!online}
          onPress={() => {
            setOpen(true);
          }}
        >
          Stop using
        </Button>
      ) : null}
      {item.status === 'inactive' ? (
        <Button
          variant="quiet"
          isDisabled={!online}
          onPress={() => {
            toggle.mutate();
          }}
        >
          Use again
        </Button>
      ) : null}
      {open ? (
        <form
          className={styles.inlineForm}
          aria-label="Stop using"
          onSubmit={(event) => {
            event.preventDefault();
            if (reason.trim().length >= 5) toggle.mutate();
          }}
        >
          <TextArea
            label="Why is it no longer used?"
            description="It can't be ordered or charged afterwards. Past charges keep it."
            value={reason}
            onChange={setReason}
          />
          <div className={styles.actions}>
            <Button type="submit" variant="destructive" isPending={toggle.isPending}>
              Stop using
            </Button>
            <Button
              variant="quiet"
              onPress={() => {
                setOpen(false);
              }}
            >
              Cancel
            </Button>
          </div>
        </form>
      ) : null}
      {toggle.error === null ? null : (
        <Notice tone="error">
          {message(toggle.error, "That change wasn't saved. Try again.")}
        </Notice>
      )}
    </div>
  );
}

function RenameItem({ item, onDone }: { item: ServiceItemDetailView; onDone: () => void }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState(item.name);
  const save = useMutation({
    mutationFn: () => updateItem(item.id, { expectedVersion: item.version, name: name.trim() }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['catalogue'] });
      toasts.show({ title: 'Details saved', description: name.trim() });
      onDone();
    },
  });
  return (
    <form
      className={styles.inlineForm}
      aria-label="Edit details"
      onSubmit={(event) => {
        event.preventDefault();
        if (name.trim().length >= 2) save.mutate();
      }}
    >
      <TextField label="Name" value={name} onChange={setName} autoFocus />
      {save.error === null ? null : (
        <Notice tone="error">{message(save.error, "It wasn't saved. Try again.")}</Notice>
      )}
      <div className={styles.actions}>
        <Button type="submit" variant="primary" isPending={save.isPending} pendingLabel="Saving…">
          Save details
        </Button>
        <Button variant="quiet" onPress={onDone}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

export function ServiceItemPage({ itemId }: { itemId: string }) {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const allowed = session !== null && can(session, 'catalogue.read');
  const online = useOnline();
  const item = useQuery({ ...itemQuery(itemId), enabled: allowed });
  const [editing, setEditing] = useState(false);

  if (!allowed) {
    return (
      <>
        <PageHeader title="Service" />
        <Notice tone="info">
          You don't have permission to see the service catalogue. Ask an administrator if you need
          it.
        </Notice>
      </>
    );
  }
  const canManage = can(session, 'catalogue.manage');
  return (
    <SettingsPage
      title={item.isSuccess ? item.data.name : 'Service'}
      intro={false}
      back={<Link to="/settings/services">All services and prices</Link>}
      {...(item.isSuccess && canManage && !editing
        ? {
            actions: (
              <Button
                isDisabled={!online}
                onPress={() => {
                  setEditing(true);
                }}
              >
                Edit details
              </Button>
            ),
          }
        : {})}
    >
      {item.isPending ? <LoadingBlock label="Loading the service" rows={4} /> : null}
      {item.isError ? (
        <Notice
          tone="error"
          action={
            <Button dense onPress={() => void item.refetch()}>
              Try again
            </Button>
          }
        >
          {message(item.error, "This service couldn't be loaded. Try again.")}
        </Notice>
      ) : null}
      {item.isSuccess ? (
        <>
          {online ? null : <Notice tone="info">Changing prices needs a connection to Wodi.</Notice>}
          {editing ? (
            <RenameItem
              item={item.data}
              onDone={() => {
                setEditing(false);
              }}
            />
          ) : null}
          <dl className={styles.details}>
            <dt>Code</dt>
            <dd className="wodi-id">{item.data.code}</dd>
            <dt>Kind</dt>
            <dd>{KIND_LABELS[item.data.kind]}</dd>
            <dt>Category</dt>
            <dd>{CATEGORY_LABELS[item.data.category]}</dd>
            <dt>Billing unit</dt>
            <dd>{item.data.billingUnit}</dd>
            <dt>Status</dt>
            <dd>
              <Tag state={item.data.status === 'active' ? 'active' : 'neutral'}>
                {item.data.status === 'active' ? 'In use' : 'Not in use'}
              </Tag>
              {item.data.statusReason === null ? null : ` ${item.data.statusReason}`}
            </dd>
          </dl>
          {canManage ? <StatusSection item={item.data} online={online} /> : null}
          <PricesSection
            item={item.data}
            canPrice={can(session, 'prices.manage')}
            online={online}
          />
          <CodesSection item={item.data} canManage={canManage} online={online} />
          <AvailabilitySection item={item.data} canManage={canManage} online={online} />
          <p className={styles.meta}>{`Shown ${formatEatDate(new Date())}.`}</p>
        </>
      ) : null}
    </SettingsPage>
  );
}
